import moment from 'moment';
import { useCallback, useContext, useMemo, useState } from 'react';

import { useLazyGetSalesPlanQuery } from '@app/api/SalePlans';
import { RIGHTS_IS_ON, RIGHTS_SALE_PLANS } from '@app/constants/accessRights';
import { WARNING } from '@app/constants/alerts';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { HeaderContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

import { generatePlanYearsRange } from '@pages/SalePlans/utils';

export const useSalePlans = () => {
  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_SALE_PLANS,
    RIGHTS_IS_ON
  );

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const { header, setHeader } = useContext(HeaderContext);
  const [getSalesPlan, { data, isFetching }] = useLazyGetSalesPlanQuery();
  const [newValue, setNewValue] = useState<number | string>(0);
  const yearHeader = header.year ? (header.year as number) : moment().year();
  const year = useMemo(() => yearHeader || moment().year(), [yearHeader]);

  const setYear = useCallback(
    (year: number | string) => setHeader({ year }),
    [setHeader]
  );

  const getSalePlansHandler = useCallback(async () => {
    try {
      await getSalesPlan({ ActiveBranchId: activeBranchId, year }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [getSalesPlan, activeBranchId, year, showNotification]);

  const selectYearHandler = useCallback(() => {
    setHeader({ year });
  }, [year, setHeader]);

  const yearOptions = useMemo(
    () => generatePlanYearsRange(data?.StartYear || year),
    [data, year]
  );

  return {
    data: data ?? null,
    year,
    newValue,
    isLoading: isFetching,
    setNewValue,
    setYear,
    hasUserRightForModuleView,
    getSalePlansHandler,
    selectYearHandler,
    yearOptions,
  };
};
